/**
 * Super Admin gate (Phase 14 / SADM-01..03 / D-01..D-07).
 *
 * Dados globais (Tribunais, etc.) NÃO têm `escritorioid` — nenhuma camada de
 * tenant-scope os protege. A verificação de permissão deste módulo é a ÚNICA
 * defesa. Por isso o gate é fail-closed em todos os pontos.
 *
 * Gate (locked, D-1.18 / SADM-03):
 *   `s.role.ativo && s.role.administracao && s.escritorioid === PRINCIPAL_ESCRITORIO_ID`
 *
 * NUNCA usa a flag `principal` da role:
 *  - `administracao` NÃO é super-permissão implícita (contrato D-1.18 — todo
 *    predicado soletra os booleanos).
 *  - a flag `principal` é POR-USUÁRIO em `escritoriousuario`, não significa
 *    "este tenant é a sede". O insumo de "qual escritório é a sede" vem 100%
 *    de env (`PRINCIPAL_ESCRITORIO_ID`), sem coluna nova no banco (SADM-03).
 *
 * O getter de env é co-localizado aqui (D / Claude's Discretion) — não há
 * módulo de env tipado separado nesta fase.
 */
import type { SessionContext } from './types';

/**
 * Resolve o `escritorioid` (char36) do escritório principal/sede a partir da
 * env `PRINCIPAL_ESCRITORIO_ID` — fail-closed (D-01 / SADM-03).
 *
 * LANÇA quando a env está ausente, vazia ou só com espaços. Como este ÚNICO
 * valor *é* o portão, um misconfig DEVE negar todos: nunca resolvemos para
 * `undefined`, que poderia casar com um `escritorioid` nulo e abrir o portão
 * por engano. Espelha o estilo fail-closed de `getBoss()` em
 * `src/shared/queue/boss-producer.ts` (lê env, lança Error citando .env.example).
 */
export function getPrincipalEscritorioId(): string {
  const value = process.env.PRINCIPAL_ESCRITORIO_ID?.trim();
  if (!value) {
    throw new Error(
      'PRINCIPAL_ESCRITORIO_ID must be set — Super Admin gate denies all access without it; see .env.example',
    );
  }
  return value;
}

/**
 * Predicado puro do Super Admin. Composição EXATA (locked):
 *   `ativo && administracao && escritorioid === PRINCIPAL_ESCRITORIO_ID`.
 *
 * NUNCA referencia a flag `principal` da role (ver nota de cabeçalho — D-1.18).
 */
export function isSuperAdmin(s: SessionContext): boolean {
  return s.role.ativo && s.role.administracao && s.escritorioid === getPrincipalEscritorioId();
}

/**
 * Permissões por-domínio do Super Admin (template `controladoria.ts`).
 *
 * D-07: TUDO (até leitura) é gated por `isSuperAdmin` nesta fase. Cada predicado
 * delega a `isSuperAdmin` (single source of edit). Relaxar `tribunalRead` para
 * usuários comuns é Future Requirement (Deferred) — fora desta fase.
 */
export const superAdminPermissions = {
  list: (s: SessionContext) => isSuperAdmin(s),
  read: (s: SessionContext) => isSuperAdmin(s),
  // Mutações do CRUD global (Phase 15 / TRIB-01..03). D-07: TUDO gated por
  // isSuperAdmin — cada predicado delega ao single source of edit. NÃO relaxar
  // nenhuma leitura para usuários comuns (relaxar `tribunalRead` é Future
  // Requirement / Deferred, fora desta fase).
  create: (s: SessionContext) => isSuperAdmin(s),
  update: (s: SessionContext) => isSuperAdmin(s),
  remove: (s: SessionContext) => isSuperAdmin(s),
};
